import {
  attachMockRecommendations,
  buildInitialStructuredDocuments,
  mapSourceDocuments,
} from '@/features/case-workspace/mockData'
import {
  classifySourceDocument,
  getSourceValue,
  labelForDocumentType,
  resolveSourceAutoPopulationStatus,
} from '@/features/case-workspace/mappings'
import { SOURCE_FIELD_MAPPINGS } from '@/features/case-workspace/mappings'
import type {
  CaseReviewState,
  DocumentSectionState,
  ReviewFinding,
  SourceDocumentRecord,
  StructuredLineItemRecord,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'
import type { Case, CaseEvent, EntryField, EntryLine, IntakeDocument, IntakeDocumentExtraction } from '@/lib/cases'
import { supabase } from '@/lib/supabase'
import { CBP_7501_LINE_ITEM_FIELDS } from '@/features/case-workspace/cbp7501'
import { COMMERCIAL_INVOICE_LINE_ITEM_FIELDS } from '@/features/case-workspace/invoiceTemplate'
import { MANIFEST_1302_SHIPMENT_FIELDS } from '@/features/case-workspace/manifest1302'
import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'

type StructuredDocumentRow = {
  id: string
  case_id: string
  document_type: StructuredDocumentRecord['documentType']
  title: string
  status: StructuredDocumentRecord['status']
  version: number
  schema_version: string
  created_by: string | null
  updated_by: string | null
  source_links: string[] | null
  field_values: StructuredDocumentRecord['fields']
  line_items?: StructuredDocumentRecord['lineItems'] | null
  section_states?: StructuredDocumentRecord['sectionStates'] | null
  validation_issues: StructuredDocumentRecord['validationIssues']
  awa_recommendations: StructuredDocumentRecord['awaRecommendations']
  audit_history: StructuredDocumentRecord['auditHistory']
  created_at: string
  updated_at: string
}

const STRUCTURED_DOCUMENT_LINE_ITEMS_FALLBACK_KEY = '__vice_line_items'
const STRUCTURED_DOCUMENT_SECTION_STATES_FALLBACK_KEY = '__vice_section_states'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function supportsStructuredDocumentExpandedColumnsError(error: { message?: string } | null) {
  if (!error?.message) return false
  return error.message.includes(`Could not find the 'line_items' column of 'structured_documents' in the schema cache`)
    || error.message.includes(`Could not find the 'section_states' column of 'structured_documents' in the schema cache`)
}

type AuditEventInsert = {
  document_id: string
  case_id: string
  actor_id: string | null
  action: string
  detail: string
  payload: Record<string, unknown>
}

function defaultSectionStates(record: Pick<StructuredDocumentRecord, 'documentType' | 'createdAt' | 'createdBy'>) {
  return Object.fromEntries(
    DOCUMENT_DEFINITIONS[record.documentType].sections.map((section) => [
      section.id,
      {
        sectionId: section.id,
        status: 'not_started',
        lastUpdatedAt: record.createdAt,
        lastUpdatedBy: record.createdBy,
      } satisfies DocumentSectionState,
    ]),
  )
}

function normalizeLegacy7501LineItems(record: {
  id: string
  createdAt: string
  createdBy: string
  lineItems?: StructuredLineItemRecord[]
}) {
  if (record.lineItems && record.lineItems.length > 0) return record.lineItems

  return [
    {
      id: `${record.id}-line-001`,
      lineNumber: '001',
      status: 'not_started',
      fields: Object.fromEntries(
        CBP_7501_LINE_ITEM_FIELDS.map((fieldDefinition) => [
          fieldDefinition.key,
          {
            value: fieldDefinition.key === 'line_number' ? '001' : '',
            displayValue: fieldDefinition.key === 'line_number' ? '001' : '',
            origin: fieldDefinition.key === 'line_number' ? 'system' : 'manual',
            reviewStatus: fieldDefinition.key === 'line_number' ? undefined : 'manually_entered',
            lastUpdatedAt: record.createdAt,
            lastUpdatedBy: record.createdBy,
          },
        ]),
      ),
      auditHistory: [],
    },
  ]
}

function normalizeLegacyManifestFields(record: {
  fields: StructuredDocumentRecord['fields']
  createdAt: string
  createdBy: string
}) {
  const fields = { ...record.fields }
  const aliases: Array<[legacyKey: string, nextKey: string]> = [
    ['bill_of_lading', 'uploaded_file_reference'],
    ['vessel_or_flight', 'vessel_name'],
    ['foreign_port', 'foreign_port_of_lading'],
  ]

  for (const [legacyKey, nextKey] of aliases) {
    if (fields[nextKey] || !fields[legacyKey]) continue
    fields[nextKey] = {
      ...fields[legacyKey],
      lastUpdatedAt: fields[legacyKey].lastUpdatedAt ?? record.createdAt,
      lastUpdatedBy: fields[legacyKey].lastUpdatedBy ?? record.createdBy,
    }
  }

  if (!fields.manifest_intake_source) {
    fields.manifest_intake_source = {
      value: 'unknown_needs_review',
      displayValue: 'unknown_needs_review',
      origin: 'system',
      lastUpdatedAt: record.createdAt,
      lastUpdatedBy: record.createdBy,
    }
  }

  return fields
}

function normalizeLegacyManifestLineItems(record: {
  id: string
  createdAt: string
  createdBy: string
  fields: StructuredDocumentRecord['fields']
  lineItems?: StructuredLineItemRecord[]
}) {
  if (record.lineItems && record.lineItems.length > 0) return record.lineItems

  return [
    {
      id: `${record.id}-shipment-001`,
      lineNumber: '001',
      status: 'not_started',
      fields: Object.fromEntries(
        MANIFEST_1302_SHIPMENT_FIELDS.map((fieldDefinition) => {
          let value = ''
          if (fieldDefinition.key === 'bill_of_lading_number') {
            value = record.fields.bill_of_lading_number?.value || record.fields.bill_of_lading?.value || ''
          } else if (fieldDefinition.key === 'goods_description') {
            value = record.fields.goods_description?.value || ''
          } else if (fieldDefinition.key === 'gross_weight') {
            value = record.fields.gross_weight?.value || ''
          } else if (fieldDefinition.key === 'consignee_name') {
            value = record.fields.consignee_name?.value || record.fields.consignee?.value || ''
          } else if (fieldDefinition.key === 'shipper_name') {
            value = record.fields.shipper_name?.value || ''
          } else if (fieldDefinition.key === 'shipment_reconciliation_status') {
            value = 'needs_manual_review'
          }

          return [
            fieldDefinition.key,
            {
              value,
              displayValue: value,
              origin: value ? 'ai_extracted' : 'manual',
              reviewStatus: value ? 'extracted' : 'manually_entered',
              lastUpdatedAt: record.createdAt,
              lastUpdatedBy: record.createdBy,
            },
          ]
        }),
      ),
      auditHistory: [],
    },
  ]
}

function normalizeLegacyInvoiceFields(record: {
  fields: StructuredDocumentRecord['fields']
  createdAt: string
  createdBy: string
}) {
  const fields = { ...record.fields }
  const aliases: Array<[legacyKey: string, nextKey: string]> = [
    ['invoice_total', 'total_invoice_value'],
    ['seller_name', 'seller_name'],
    ['buyer_name', 'buyer_name'],
  ]

  for (const [legacyKey, nextKey] of aliases) {
    if (fields[nextKey] || !fields[legacyKey]) continue
    fields[nextKey] = {
      ...fields[legacyKey],
      lastUpdatedAt: fields[legacyKey].lastUpdatedAt ?? record.createdAt,
      lastUpdatedBy: fields[legacyKey].lastUpdatedBy ?? record.createdBy,
    }
  }

  if (!fields.document_source) {
    fields.document_source = {
      value: 'unknown_needs_review',
      displayValue: 'unknown_needs_review',
      origin: 'system',
      lastUpdatedAt: record.createdAt,
      lastUpdatedBy: record.createdBy,
    }
  }

  return fields
}

function normalizeLegacyInvoiceLineItems(record: {
  id: string
  createdAt: string
  createdBy: string
  fields: StructuredDocumentRecord['fields']
  lineItems?: StructuredLineItemRecord[]
}) {
  if (record.lineItems && record.lineItems.length > 0) return record.lineItems

  return [
    {
      id: `${record.id}-invoice-line-001`,
      lineNumber: '001',
      status: 'not_started',
      fields: Object.fromEntries(
        COMMERCIAL_INVOICE_LINE_ITEM_FIELDS.map((fieldDefinition) => {
          let value = ''
          if (fieldDefinition.key === 'line_number') {
            value = '001'
          } else if (fieldDefinition.key === 'detailed_merchandise_description') {
            value = record.fields.purchase_price_statement?.value || ''
          } else if (fieldDefinition.key === 'extended_line_value') {
            value = record.fields.total_invoice_value?.value || record.fields.invoice_total?.value || ''
          } else if (fieldDefinition.key === 'currency_line') {
            value = record.fields.currency_code?.value || 'USD'
          } else if (fieldDefinition.key === 'line_discrepancy_status') {
            value = 'needs_manual_review'
          }

          return [
            fieldDefinition.key,
            {
              value,
              displayValue: value,
              origin: value ? 'ai_extracted' : fieldDefinition.key === 'line_number' ? 'system' : 'manual',
              reviewStatus: value ? 'extracted' : fieldDefinition.key === 'line_number' ? undefined : 'manually_entered',
              lastUpdatedAt: record.createdAt,
              lastUpdatedBy: record.createdBy,
            },
          ]
        }),
      ),
      auditHistory: [],
    },
  ]
}

function detectManifestIntakeSource(params: {
  doc: IntakeDocument
  extraction?: IntakeDocumentExtraction
}) {
  const { doc, extraction } = params
  const lower = doc.original_filename.toLowerCase()
  const text = `${lower} ${(doc.ocr_text ?? '').toLowerCase()} ${JSON.stringify(extraction?.extracted_fields ?? {})}`.toLowerCase()
  if (text.includes('ace')) return 'ace_direct_transmission'
  if (text.includes('vecs')) return 'vecs_submission'
  if (text.includes('paper') || text.includes('arrival') || text.includes('provided upon arrival') || text.includes('1302')) {
    return 'port_arrival_document'
  }
  return 'uploaded_external_source'
}

const MANIFEST_INTAKE_PLACEHOLDERS = new Set([
  '',
  'unknown_needs_review',
  'uploaded_external_source',
])

function updateFieldIfChanged(params: {
  record: StructuredDocumentRecord
  fieldKey: string
  nextValue: string
  actorLabel: string
  origin?: StructuredDocumentRecord['fields'][string]['origin']
  sourceDocumentId?: string
  sourceDocumentTitle?: string
  sourceConfidence?: number | null
  onlyReplacePlaceholder?: boolean
}) {
  const {
    record,
    fieldKey,
    nextValue,
    actorLabel,
    origin = 'system',
    sourceDocumentId,
    sourceDocumentTitle,
    sourceConfidence,
    onlyReplacePlaceholder = false,
  } = params

  const currentField = record.fields[fieldKey]
  if (!currentField) return false

  if (onlyReplacePlaceholder && !MANIFEST_INTAKE_PLACEHOLDERS.has(currentField.value)) {
    return false
  }

  const normalizedNextValue = nextValue ?? ''
  const normalizedNextConfidence = sourceConfidence ?? null
  const currentConfidence = currentField.sourceConfidence ?? null
  const sourceIdChanged = (currentField.sourceDocumentId ?? null) !== (sourceDocumentId ?? null)
  const sourceTitleChanged = (currentField.sourceDocumentTitle ?? null) !== (sourceDocumentTitle ?? null)

  if (
    currentField.value === normalizedNextValue
    && currentField.displayValue === normalizedNextValue
    && currentField.origin === origin
    && currentConfidence === normalizedNextConfidence
    && !sourceIdChanged
    && !sourceTitleChanged
  ) {
    return false
  }

  record.fields[fieldKey] = {
    ...currentField,
    value: normalizedNextValue,
    displayValue: normalizedNextValue,
    origin,
    sourceDocumentId,
    sourceDocumentTitle,
    sourceConfidence: normalizedNextConfidence ?? undefined,
    lastUpdatedAt: new Date().toISOString(),
    lastUpdatedBy: actorLabel,
  }

  return true
}

function unlinkRemovedSourceDocuments(params: {
  records: StructuredDocumentRecord[]
  removedSourceDocuments: SourceDocumentRecord[]
  actorLabel: string
}) {
  const { records, removedSourceDocuments, actorLabel } = params
  const removedIds = new Set(removedSourceDocuments.map((doc) => doc.id))
  const removedNames = new Map(removedSourceDocuments.map((doc) => [doc.id, doc.title]))

  return records.map((record) => {
    const nextSourceLinks = record.sourceLinks.filter((sourceId) => !removedIds.has(sourceId))
    let changed = nextSourceLinks.length !== record.sourceLinks.length

    const nextFields = Object.fromEntries(
      Object.entries(record.fields).map(([fieldKey, fieldState]) => {
        if (!fieldState.sourceDocumentId || !removedIds.has(fieldState.sourceDocumentId)) {
          return [fieldKey, fieldState]
        }

        changed = true
        const removedName = removedNames.get(fieldState.sourceDocumentId) ?? 'the removed file'

        return [
          fieldKey,
          {
            ...fieldState,
            sourceDocumentId: undefined,
            sourceDocumentTitle: undefined,
            extractedFieldKey: undefined,
            sourceValue: undefined,
            conflictNote: `The source file ${removedName} was removed from the case. Review this value before continuing.`,
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedBy: actorLabel,
          },
        ]
      }),
    )

    if (!changed) return record

    return {
      ...record,
      sourceLinks: nextSourceLinks,
      fields: nextFields,
      auditHistory: [
        {
          id: `${record.id}-removed-source-${Date.now()}`,
          at: new Date().toISOString(),
          actor: actorLabel,
          action: 'source_file_removed',
          detail:
            removedSourceDocuments.length === 1
              ? `${removedSourceDocuments[0].title} was removed from the case and unlinked from ${record.title}.`
              : `${removedSourceDocuments.length} source files were removed from the case and unlinked from ${record.title}.`,
        },
        ...record.auditHistory,
      ],
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: actorLabel,
    }
  })
}

function rowToRecord(row: StructuredDocumentRow, actorFallback: string) {
  const fallbackLineItems = Array.isArray(row.field_values?.[STRUCTURED_DOCUMENT_LINE_ITEMS_FALLBACK_KEY])
    ? (row.field_values[STRUCTURED_DOCUMENT_LINE_ITEMS_FALLBACK_KEY] as StructuredDocumentRecord['lineItems'])
    : undefined
  const fallbackSectionStates = isPlainObject(row.field_values?.[STRUCTURED_DOCUMENT_SECTION_STATES_FALLBACK_KEY])
    ? (row.field_values[STRUCTURED_DOCUMENT_SECTION_STATES_FALLBACK_KEY] as StructuredDocumentRecord['sectionStates'])
    : undefined
  const rawFields = { ...(row.field_values ?? {}) }
  delete rawFields[STRUCTURED_DOCUMENT_LINE_ITEMS_FALLBACK_KEY]
  delete rawFields[STRUCTURED_DOCUMENT_SECTION_STATES_FALLBACK_KEY]
  const normalizedFields =
    row.document_type === 'manifest'
      ? normalizeLegacyManifestFields({
          fields: rawFields,
          createdAt: row.created_at,
          createdBy: row.created_by ?? actorFallback,
        })
      : row.document_type === 'commercial_invoice'
        ? normalizeLegacyInvoiceFields({
            fields: rawFields,
            createdAt: row.created_at,
            createdBy: row.created_by ?? actorFallback,
          })
      : rawFields

  const baseRecord = {
    id: row.id,
    caseId: row.case_id,
    kind: 'structured' as const,
    documentType: row.document_type,
    title: row.title,
    status: row.status,
    version: row.version,
    schemaVersion: row.schema_version,
    createdAt: row.created_at,
    createdBy: row.created_by ?? actorFallback,
    lastUpdatedAt: row.updated_at,
    lastUpdatedBy: row.updated_by ?? actorFallback,
    sourceLinks: row.source_links ?? [],
    fields: normalizedFields,
    lineItems: row.line_items ?? fallbackLineItems ?? [],
    sectionStates: row.section_states ?? fallbackSectionStates ?? {},
    validationIssues: row.validation_issues ?? [],
    awaRecommendations: row.awa_recommendations ?? [],
    auditHistory: row.audit_history ?? [],
  }

  return {
    ...baseRecord,
    lineItems:
      baseRecord.documentType === 'cbp_7501'
        ? normalizeLegacy7501LineItems({
            id: baseRecord.id,
            createdAt: baseRecord.createdAt,
            createdBy: baseRecord.createdBy,
            lineItems: baseRecord.lineItems,
          })
        : baseRecord.documentType === 'manifest'
          ? normalizeLegacyManifestLineItems({
              id: baseRecord.id,
              createdAt: baseRecord.createdAt,
              createdBy: baseRecord.createdBy,
              fields: baseRecord.fields,
              lineItems: baseRecord.lineItems,
            })
          : baseRecord.documentType === 'commercial_invoice'
            ? normalizeLegacyInvoiceLineItems({
                id: baseRecord.id,
                createdAt: baseRecord.createdAt,
                createdBy: baseRecord.createdBy,
                fields: baseRecord.fields,
                lineItems: baseRecord.lineItems,
              })
        : baseRecord.lineItems,
    sectionStates:
      Object.keys(baseRecord.sectionStates).length > 0
        ? baseRecord.sectionStates
        : defaultSectionStates({
            documentType: baseRecord.documentType,
            createdAt: baseRecord.createdAt,
            createdBy: baseRecord.createdBy,
          }),
  } satisfies StructuredDocumentRecord
}

function recordToRow(record: StructuredDocumentRecord, actorId?: string) {
  return {
    id: record.id,
    case_id: record.caseId,
    document_type: record.documentType,
    title: record.title,
    status: record.status,
    version: record.version,
    schema_version: record.schemaVersion,
    // created_by is intentionally omitted — the DB default handles it
    // on INSERT, and we must not overwrite it on UPDATE (upsert).
    updated_by: actorId ?? null,
    source_links: record.sourceLinks,
    field_values: record.fields,
    line_items: record.lineItems,
    section_states: record.sectionStates,
    validation_issues: record.validationIssues,
    awa_recommendations: record.awaRecommendations,
    audit_history: record.auditHistory,
    created_at: record.createdAt,
    updated_at: record.lastUpdatedAt,
  }
}

function recordToLegacyCompatibleRow(record: StructuredDocumentRecord, actorId?: string) {
  return {
    id: record.id,
    case_id: record.caseId,
    document_type: record.documentType,
    title: record.title,
    status: record.status,
    version: record.version,
    schema_version: record.schemaVersion,
    updated_by: actorId ?? null,
    source_links: record.sourceLinks,
    field_values: {
      ...record.fields,
      [STRUCTURED_DOCUMENT_LINE_ITEMS_FALLBACK_KEY]: record.lineItems,
      [STRUCTURED_DOCUMENT_SECTION_STATES_FALLBACK_KEY]: record.sectionStates,
    },
    validation_issues: record.validationIssues,
    awa_recommendations: record.awaRecommendations,
    audit_history: record.auditHistory,
    created_at: record.createdAt,
    updated_at: record.lastUpdatedAt,
  }
}

async function upsertStructuredDocuments(records: StructuredDocumentRecord[], actorId?: string) {
  const primaryResponse = await supabase
    .from('structured_documents')
    .upsert(records.map((record) => recordToRow(record, actorId)), { onConflict: 'id' })

  if (!supportsStructuredDocumentExpandedColumnsError(primaryResponse.error)) {
    return primaryResponse
  }

  return supabase
    .from('structured_documents')
    .upsert(records.map((record) => recordToLegacyCompatibleRow(record, actorId)), { onConflict: 'id' })
}

async function upsertStructuredDocument(record: StructuredDocumentRecord, actorId?: string) {
  const primaryResponse = await supabase
    .from('structured_documents')
    .upsert(recordToRow(record, actorId), { onConflict: 'id' })
    .select('*')
    .single()

  if (!supportsStructuredDocumentExpandedColumnsError(primaryResponse.error)) {
    return primaryResponse
  }

  return supabase
    .from('structured_documents')
    .upsert(recordToLegacyCompatibleRow(record, actorId), { onConflict: 'id' })
    .select('*')
    .single()
}

async function fetchStructuredDocuments(caseId: string, actorFallback: string) {
  const { data, error } = await supabase
    .from('structured_documents')
    .select('*')
    .eq('case_id', caseId)
    .order('document_type', { ascending: true })
    .order('version', { ascending: true })

  if (error) throw error

  return ((data ?? []) as StructuredDocumentRow[]).map((row) => rowToRecord(row, actorFallback))
}

async function fetchDocumentExtractions(caseId: string) {
  const { data, error } = await supabase
    .from('intake_document_extractions')
    .select('*')
    .eq('case_id', caseId)
    .order('updated_at', { ascending: false })

  if (error) throw error

  return (data ?? []) as IntakeDocumentExtraction[]
}

function shouldApplyAutoPopulation(record: StructuredDocumentRecord, fieldKey: string, incomingValue: string) {
  const existing = record.fields[fieldKey]
  if (!existing) return { shouldApply: false, reason: 'field_missing' as const }
  if (!incomingValue) return { shouldApply: false, reason: 'no_value' as const }
  if (!existing.value) return { shouldApply: true, reason: 'empty' as const }
  // Skip if value is unchanged — avoids duplicate audit entries and
  // misleading lastUpdatedAt churn on every refresh.
  if (existing.value === incomingValue) {
    return { shouldApply: false, reason: 'same_value' as const }
  }
  if (existing.origin === 'ai_extracted' || existing.origin === 'uploaded_file') {
    return { shouldApply: true, reason: 'replace_extracted' as const }
  }
  return { shouldApply: false, reason: 'protected_value' as const }
}

function syncSourceDocuments(params: {
  kase: Case
  actorLabel: string
  intakeDocuments: IntakeDocument[]
  documentExtractions: IntakeDocumentExtraction[]
  fields: EntryField[]
  lines: EntryLine[]
  structuredDocuments: StructuredDocumentRecord[]
}) {
  const { kase, actorLabel, intakeDocuments, documentExtractions, fields, lines, structuredDocuments } = params
  const nextStructuredDocuments = structuredDocuments.map((record) => ({
    ...record,
    sourceLinks: [...record.sourceLinks],
    fields: Object.fromEntries(Object.entries(record.fields).map(([key, value]) => [key, { ...value }])),
    lineItems: record.lineItems.map((lineItem) => ({
      ...lineItem,
      fields: Object.fromEntries(Object.entries(lineItem.fields).map(([key, value]) => [key, { ...value }])),
      auditHistory: [...lineItem.auditHistory],
    })),
    sectionStates: Object.fromEntries(
      Object.entries(record.sectionStates).map(([sectionId, state]) => [sectionId, { ...state }]),
    ),
    auditHistory: [...record.auditHistory],
    awaRecommendations: [...record.awaRecommendations],
  }))

  const sourceDocuments = intakeDocuments.map((doc) => {
    const extraction = documentExtractions.find((row) => row.intake_document_id === doc.id)
    const classification = extraction?.detected_document_type
      ? {
          documentType: extraction.detected_document_type as StructuredDocumentRecord['documentType'],
          label: labelForDocumentType(extraction.detected_document_type as StructuredDocumentRecord['documentType']),
          method: extraction.classification_method as SourceDocumentRecord['classificationMethod'],
        }
      : classifySourceDocument(doc)
    const linkedRecord = classification.documentType
      ? nextStructuredDocuments.find((record) => record.documentType === classification.documentType)
      : undefined
    let autoPopulatedFieldCount = 0
    let conflictFieldCount = 0

    if (linkedRecord && classification.documentType && doc.ocr_status === 'done') {
      if (!linkedRecord.sourceLinks.includes(doc.id)) {
        linkedRecord.sourceLinks.push(doc.id)
      }

      if (classification.documentType === 'manifest') {
        const intakeSource = detectManifestIntakeSource({ doc, extraction })
        const confidenceValue =
          extraction?.extraction_confidence == null && doc.ocr_confidence == null
            ? ''
            : String(extraction?.extraction_confidence ?? doc.ocr_confidence)

        updateFieldIfChanged({
          record: linkedRecord,
          fieldKey: 'manifest_intake_source',
          nextValue: intakeSource,
          actorLabel,
          sourceDocumentId: doc.id,
          sourceDocumentTitle: doc.original_filename,
          sourceConfidence: extraction?.extraction_confidence ?? doc.ocr_confidence,
          onlyReplacePlaceholder: true,
        })
        updateFieldIfChanged({
          record: linkedRecord,
          fieldKey: 'uploaded_file_reference',
          nextValue: doc.original_filename,
          actorLabel,
          sourceDocumentId: doc.id,
          sourceDocumentTitle: doc.original_filename,
          sourceConfidence: extraction?.extraction_confidence ?? doc.ocr_confidence,
        })
        updateFieldIfChanged({
          record: linkedRecord,
          fieldKey: 'ocr_confidence',
          nextValue: confidenceValue,
          actorLabel,
        })
        updateFieldIfChanged({
          record: linkedRecord,
          fieldKey: 'system_confidence',
          nextValue: confidenceValue,
          actorLabel,
        })
      }

      for (const mapping of SOURCE_FIELD_MAPPINGS[classification.documentType]) {
        const incomingValue = getSourceValue({
          kase,
          fields,
          lines,
          extraction,
          sourceFieldKey: mapping.sourceFieldKey,
        })
        const resolution = shouldApplyAutoPopulation(linkedRecord, mapping.targetFieldKey, incomingValue)

        if (resolution.shouldApply) {
          linkedRecord.fields[mapping.targetFieldKey] = {
            ...linkedRecord.fields[mapping.targetFieldKey],
            value: incomingValue,
            displayValue: incomingValue,
            origin: 'ai_extracted',
            sourceValue: incomingValue,
            sourceConfidence: extraction?.extraction_confidence ?? doc.ocr_confidence,
            sourceDocumentId: doc.id,
            sourceDocumentTitle: doc.original_filename,
            extractedFieldKey: mapping.sourceFieldKey,
            conflictNote: undefined,
            lastUpdatedAt: new Date().toISOString(),
            lastUpdatedBy: actorLabel,
          }
          autoPopulatedFieldCount += 1
        } else if (resolution.reason === 'protected_value' && incomingValue) {
          linkedRecord.fields[mapping.targetFieldKey] = {
            ...linkedRecord.fields[mapping.targetFieldKey],
            sourceDocumentId: doc.id,
            sourceDocumentTitle: doc.original_filename,
            extractedFieldKey: mapping.sourceFieldKey,
            sourceConfidence: extraction?.extraction_confidence ?? doc.ocr_confidence,
            conflictNote: `Source document suggests ${incomingValue}, but the current reviewed value was preserved.`,
          }
          conflictFieldCount += 1
        }
      }

      if (autoPopulatedFieldCount > 0 || conflictFieldCount > 0) {
        linkedRecord.auditHistory = [
          {
            id: `${linkedRecord.id}-${doc.id}-sync-${Date.now()}`,
            at: new Date().toISOString(),
            actor: 'VICE',
            action: 'source_document_synced',
            detail:
              autoPopulatedFieldCount > 0
                ? `${doc.original_filename} populated ${autoPopulatedFieldCount} field${autoPopulatedFieldCount === 1 ? '' : 's'} in ${linkedRecord.title}.`
                : `${doc.original_filename} matched ${linkedRecord.title}, but reviewed values were preserved where conflicts existed.`,
          },
          ...linkedRecord.auditHistory,
        ]
      }
    }

    return {
      ...mapSourceDocuments([doc], actorLabel)[0],
      mirrorsDocumentType: classification.documentType,
      linkedStructuredDocumentId: linkedRecord?.id ?? null,
      classificationMethod: classification.method,
      autoPopulatedFieldCount,
      conflictFieldCount,
      autoPopulationStatus: resolveSourceAutoPopulationStatus({
        extractionStatus: doc.ocr_status,
        mirrorsDocumentType: classification.documentType,
        autoPopulatedFieldCount,
        conflictFieldCount,
      }),
    } satisfies SourceDocumentRecord
  })

  return { sourceDocuments, structuredDocuments: nextStructuredDocuments }
}

export const caseWorkspaceService = {
  async ensureStructuredDocuments(params: {
    kase: Case
    actorId?: string
    actorLabel: string
    intakeDocuments: IntakeDocument[]
    fields: EntryField[]
    lines: EntryLine[]
  }) {
    const { kase, actorId, actorLabel, intakeDocuments, fields, lines } = params
    const documentExtractions = await fetchDocumentExtractions(kase.id)

    const existingRecords = await fetchStructuredDocuments(kase.id, actorLabel)
    if (existingRecords.length > 0) {
      const syncedExisting = syncSourceDocuments({
        kase,
        actorLabel,
        intakeDocuments,
        documentExtractions,
        fields,
        lines,
        structuredDocuments: existingRecords,
      })

      const hasSourceLinkUpdates = syncedExisting.structuredDocuments.some((record, index) => {
        const original = existingRecords[index]
        return JSON.stringify(original.sourceLinks) !== JSON.stringify(record.sourceLinks)
          || JSON.stringify(original.fields) !== JSON.stringify(record.fields)
          || JSON.stringify(original.auditHistory) !== JSON.stringify(record.auditHistory)
      })

      if (hasSourceLinkUpdates) {
        const { error: updateError } = await upsertStructuredDocuments(syncedExisting.structuredDocuments, actorId)
        if (updateError) throw updateError
      }

      return syncedExisting
    }

    const seededRecords = attachMockRecommendations(
      buildInitialStructuredDocuments({
        kase,
        actorLabel,
        fields,
        lines,
        sourceDocs: mapSourceDocuments(intakeDocuments, actorLabel),
      }),
    )

      const syncedSeeded = syncSourceDocuments({
        kase,
        actorLabel,
        intakeDocuments,
        documentExtractions,
        fields,
        lines,
        structuredDocuments: seededRecords,
    })

    const { error: insertError } = await upsertStructuredDocuments(syncedSeeded.structuredDocuments, actorId)

    if (insertError) throw insertError

    const auditInserts: AuditEventInsert[] = syncedSeeded.structuredDocuments.flatMap((record) =>
      record.auditHistory.slice(0, 2).map((entry) => ({
        document_id: record.id,
        case_id: record.caseId,
        actor_id: actorId ?? null,
        action: entry.action,
        detail: entry.detail,
        payload: { seeded: true },
      })),
    )

    if (auditInserts.length > 0) {
      await supabase.from('structured_document_audit_events').insert(auditInserts)
    }

    return {
      sourceDocuments: syncedSeeded.sourceDocuments,
      structuredDocuments: await fetchStructuredDocuments(kase.id, actorLabel),
    }
  },

  async saveStructuredDocument(params: {
    record: StructuredDocumentRecord
    actorId?: string
    actorLabel: string
  }) {
    const { record, actorId, actorLabel } = params

    const { data, error } = await upsertStructuredDocument(record, actorId)

    if (error) throw error

    const latestAudit = record.auditHistory[0]

    if (latestAudit) {
      await supabase.from('structured_document_audit_events').insert({
        document_id: record.id,
        case_id: record.caseId,
        actor_id: actorId ?? null,
        action: latestAudit.action,
        detail: latestAudit.detail,
        payload: { source: 'documents_workspace' },
      })
    }

    const { data: eventData, error: eventError } = await supabase.from('case_events').insert({
      case_id: record.caseId,
      actor_id: actorId ?? null,
      kind: 'structured_document.saved',
      payload: {
        document_id: record.id,
        document_type: record.documentType,
        status: record.status,
        actor_label: actorLabel,
      },
    }).select('*').single()

    if (eventError) throw eventError

    return {
      record: rowToRecord(data as StructuredDocumentRow, actorLabel),
      event: eventData as CaseEvent,
    }
  },

  async initiateAwaReview(params: {
    reviewState: CaseReviewState
    actorId?: string
    actorLabel: string
  }) {
    const { reviewState, actorId, actorLabel } = params
    const findings: ReviewFinding[] = reviewState.findings.map((finding, index) =>
      index === 0
        ? { ...finding, status: 'investigating' }
        : finding,
    )

    const nextState = {
      ...reviewState,
      awaStatus: 'in_progress' as const,
      initiatedAt: new Date().toISOString(),
      initiatedBy: actorLabel,
      findings,
      reviewHistory: [
        {
          id: `${reviewState.caseId}-review-started`,
          at: new Date().toISOString(),
          actor: actorLabel,
          action: 'awa_review_started',
          detail: 'AWA review initiated from the Review tab.',
        },
        ...reviewState.reviewHistory,
      ],
    }

    const openFindings = nextState.findings.filter((finding) => finding.status !== 'resolved').length
    const highPriorityFindings = nextState.findings.filter(
      (finding) => (finding.severity === 'high' || finding.severity === 'blocks_acceptance') && finding.status !== 'resolved',
    ).length

    const { data: eventData, error: eventError } = await supabase.from('case_events').insert({
      case_id: reviewState.caseId,
      actor_id: actorId ?? null,
      kind: 'awa.review_initiated',
      payload: {
        actor_label: actorLabel,
        total_findings: nextState.findings.length,
        open_findings: openFindings,
        high_priority_findings: highPriorityFindings,
      },
    }).select('*').single()

    if (eventError) throw eventError

    return {
      reviewState: nextState,
      event: eventData as CaseEvent,
    }
  },

  async removeSourceDocuments(params: {
    caseId: string
    documents: SourceDocumentRecord[]
    structuredDocuments: StructuredDocumentRecord[]
    actorId?: string
    actorLabel: string
  }) {
    const { caseId, documents, structuredDocuments, actorId, actorLabel } = params
    if (documents.length === 0) {
      return { removedIds: [], structuredDocuments, event: null as CaseEvent | null }
    }

    const documentIds = documents.map((document) => document.id)
    const nextStructuredDocuments = unlinkRemovedSourceDocuments({
      records: structuredDocuments,
      removedSourceDocuments: documents,
      actorLabel,
    })

    const structuredDocumentsChanged = nextStructuredDocuments.some(
      (record, index) =>
        JSON.stringify(record.sourceLinks) !== JSON.stringify(structuredDocuments[index]?.sourceLinks)
        || JSON.stringify(record.fields) !== JSON.stringify(structuredDocuments[index]?.fields)
        || JSON.stringify(record.auditHistory) !== JSON.stringify(structuredDocuments[index]?.auditHistory),
    )

    if (structuredDocumentsChanged) {
      const { error: structuredError } = await upsertStructuredDocuments(nextStructuredDocuments, actorId)
      if (structuredError) throw structuredError
    }

    const { error: deleteError } = await supabase
      .from('intake_documents')
      .delete()
      .eq('case_id', caseId)
      .in('id', documentIds)

    if (deleteError) throw deleteError

    const { data: eventData, error: eventError } = await supabase
      .from('case_events')
      .insert({
        case_id: caseId,
        actor_id: actorId ?? null,
        kind: 'document.removed',
        payload: {
          actor_label: actorLabel,
          removed_count: documents.length,
          filenames: documents.map((document) => document.title),
        },
      })
      .select('*')
      .single()

    if (eventError) throw eventError

    const storagePaths = documents
      .map((document) => document.storagePath)
      .filter((path): path is string => Boolean(path))

    if (storagePaths.length > 0) {
      const { error: storageError } = await supabase.storage.from('intake-documents').remove(storagePaths)
      if (storageError) {
        console.warn('[vice] failed to clean up removed upload from storage:', storageError.message)
      }
    }

    return {
      removedIds: documentIds,
      structuredDocuments: nextStructuredDocuments,
      event: eventData as CaseEvent,
    }
  },
}
